import { yupResolver } from '@hookform/resolvers/yup'
import iconQr from 'assets/icons/iconQr.svg'
import * as Common from 'components/common/Common'
import { KioskViewContainer, transparentCss } from 'components/common/Common'
import NextButton from 'components/common/NextButton'
import * as Typography from 'components/common/TypographyKiosk'
import InputText from 'components/inputs/InputText'
import {
  ROUTE_CHECKIN_CONFIRM_EMAIL,
  ROUTE_CHECKIN_FIND_RESERVATION,
  ROUTE_CHECKOUT_CONFIRM_EMAIL,
  ROUTE_CHECKOUT_FIND_RESERVATION,
} from 'constants/routes'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import Keyboard from 'react-simple-keyboard'
import 'react-simple-keyboard/build/css/index.css'
import { selectIsKiosk } from 'redux/slices/app'
import {
  selectReservationDetails,
  selectReservationError,
  selectReservationStatus,
} from 'redux/slices/reservation/reservationSelectors'
import { fetchReservationDetails } from 'redux/slices/reservation/reservationSlice'
import {
  IFindReservationForm,
  notify,
  searchSchema,
  ToastTypes,
} from 'shared-ui'
import styled, { css, useTheme } from 'styled-components'

export const FormContainer = styled(Common.Div)(
  ({ theme: { colors, params } }) => css`
    padding: 25px 30px;
    height: 202px;
    width: 920px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border: 1px solid ${colors.border};
    gap: 42px;
    border-radius: ${params.buttonBorderRadius};
    ${transparentCss};
  `
)

const ViewCheckInOutReservationNumber = () => {
  const { t } = useTranslation()
  const searchForm = useForm<IFindReservationForm>({
    defaultValues: {
      resNumber: '',
    },
    resolver: yupResolver(searchSchema(t)),
  })

  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isKiosk = useSelector(selectIsKiosk)
  const { pathname } = useLocation()
  const reservationStatus = useSelector(selectReservationStatus)
  const reservationDetails = useSelector(selectReservationDetails)
  const reservationError = useSelector(selectReservationError)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [keyboardVisible, setKeyboardVisible] = useState(false)
  const [resNumber, setResNumber] = useState<string>(
    searchForm.watch('resNumber') || ''
  )

  useEffect(() => {
    if (!keyboardVisible) {
      setResNumber(searchForm.getValues('resNumber') || '')
    }
  }, [keyboardVisible, searchForm])

  const handleSearch: SubmitHandler<IFindReservationForm> = (
    data: IFindReservationForm
  ) => {
    dispatch(fetchReservationDetails(data.resNumber))
  }

  const updateInputValue = (input: string) => {
    setResNumber(input)
    searchForm.setValue('resNumber', input)
  }

  const handleKeyPress = (key: string) => {
    if (key === '{bksp}') {
      setResNumber(prev => prev.slice(0, -1))
      searchForm.setValue('resNumber', resNumber.slice(0, -1))
    } else {
      setResNumber(prev => prev + key)
      searchForm.setValue('resNumber', resNumber + key)
    }
  }

  useEffect(() => {
    if (reservationStatus === 'succeeded') {
      if (pathname === ROUTE_CHECKIN_FIND_RESERVATION) {
        navigate(ROUTE_CHECKIN_CONFIRM_EMAIL)
      } else if (pathname === ROUTE_CHECKOUT_FIND_RESERVATION) {
        navigate(ROUTE_CHECKOUT_CONFIRM_EMAIL)
      }
    } else if (reservationStatus === 'failed') {
      notify(reservationError)[ToastTypes.ERROR]()
    }
  }, [reservationStatus, reservationDetails, navigate, pathname])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setKeyboardVisible(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <KioskViewContainer bgUrl={theme.assets.searchBackground}>
      <Typography.SubWelcomeHeader
        mt={15}
        mb={10}
        align="center"
        tertiary
      >
        {t('findReservation.header')}
        <br />
        {t('findReservation.subHeader')}
      </Typography.SubWelcomeHeader>

      <Common.Div
        flex="row"
        justifyContent="center"
        w="100%"
      >
        <FormContainer>
          <Common.Div
            flex="column"
            gap="42px"
            w="100%"
          >
            <form onSubmit={searchForm.handleSubmit(handleSearch)}>
              <Common.Div
                position="relative"
                flex="row"
                gap="16px"
                ref={wrapperRef}
              >
                <InputText
                  form={searchForm}
                  name="resNumber"
                  onClick={() => setKeyboardVisible(true)}
                  placeholder={t('findReservation.numberPlaceholder')}
                  secondary
                  type="text"
                  w="100%"
                  autoFocus
                />
                {keyboardVisible && isKiosk && (
                  <Common.Div
                    position="absolute"
                    top={65}
                    w="360px"
                  >
                    <Keyboard
                      baseClass="keyboardResNumber"
                      onChange={(input: string) => updateInputValue(input)}
                      onKeyPress={handleKeyPress}
                      inputName="resNumber"
                      layout={{
                        default: ['1 2 3', '4 5 6', '7 8 9', '* 0 #', '{bksp}'],
                      }}
                      display={{ '{bksp}': '⌫' }}
                    />
                  </Common.Div>
                )}
                <NextButton
                  type="submit"
                  disabled={reservationStatus === 'loading'}
                />
              </Common.Div>
            </form>
            {isKiosk && (
              <Common.Div
                flex="row"
                gap="36px"
              >
                <Common.SvgIcon
                  src={iconQr}
                  w="40px"
                  h="40px"
                />
                <Typography.Body secondary>
                  <Trans i18nKey="findReservation.qrDesc" />
                </Typography.Body>
              </Common.Div>
            )}
          </Common.Div>
        </FormContainer>
      </Common.Div>
    </KioskViewContainer>
  )
}

export default ViewCheckInOutReservationNumber
